import { Box, Checkbox, CircularProgress, FormControlLabel, Typography } from '@material-ui/core';
import { FormikProps } from 'formik';
import React, { useRef, useState } from 'react';
import { translate } from '../../../../common/intl';
import { AsyncState, AsyncStatus } from '../../../../state/types';
import { useStyles } from './WhiteLabelingManagement.styles';

interface Props extends FormikProps<any>{
  onLogoChange: (event: React.FormEvent<HTMLInputElement>) => void
  onAvatarChange: (event: React.FormEvent<HTMLInputElement>) => void
  onPoLogoChange: (event: React.FormEvent<HTMLInputElement>) => void
  onPoLogoSameAsLogo: (checked: boolean) => void
  asyncState: AsyncState
  asyncS3linkState: AsyncState
  whiteLabel: { logo: string, avatar: string, poLogo?: string }
}

interface FileUploadInfo {
  name: string
  size: string
}

interface ImageDimensions {
  width: number
  height: number
}

const getImageDimensions = async (file: File): Promise<ImageDimensions> => {
  return await new Promise((resolve, reject) => {
    const objectUrl = URL.createObjectURL(file);
    const image = new Image();
    image.onload = () => {
      URL.revokeObjectURL(objectUrl);
      resolve({ width: image.naturalWidth, height: image.naturalHeight });
    };
    image.onerror = () => {
      URL.revokeObjectURL(objectUrl);
      reject(new Error(translate('administration.company.information.whiteLabeling.unableToReadImage')));
    };
    image.src = objectUrl;
  });
};

const LOGO_DIMENSIONS: ImageDimensions = { width: 180, height: 75 };
const AVATAR_DIMENSIONS: ImageDimensions = { width: 58, height: 58 };
const PO_LOGO_DIMENSIONS: ImageDimensions = { width: 180, height: 75 };

const ASPECT_RATIO_TOLERANCE = 0.02;

const matchesAspectRatio = (dimensions: ImageDimensions, expected: ImageDimensions): boolean => {
  const actualRatio = dimensions.width / dimensions.height;
  const expectedRatio = expected.width / expected.height;
  return Math.abs(actualRatio - expectedRatio) <= ASPECT_RATIO_TOLERANCE;
};

const WhiteLabelingManagementPresenter: React.FunctionComponent<Props> = ({
  dirty,
  resetForm,
  initialValues,
  onLogoChange,
  onAvatarChange,
  onPoLogoChange,
  onPoLogoSameAsLogo,
  asyncState,
  asyncS3linkState,
  whiteLabel,
}) => {
  const classes = useStyles();
  const logoInputRef = useRef<HTMLInputElement>(null);
  const avatarInputRef = useRef<HTMLInputElement>(null);
  const poLogoInputRef = useRef<HTMLInputElement>(null);
  const [logoUploadedFile, setLogoUploadedFile] = useState<FileUploadInfo | null>(null);
  const [avatarUploadedFile, setAvatarUploadedFile] = useState<FileUploadInfo | null>(null);
  const [poLogoUploadedFile, setPoLogoUploadedFile] = useState<FileUploadInfo | null>(null);
  const [isPoLogoSameAsLogo, setIsPoLogoSameAsLogo] = useState(false);
  const [logoError, setLogoError] = useState<string | null>(null);
  const [avatarError, setAvatarError] = useState<string | null>(null);
  const [poLogoError, setPoLogoError] = useState<string | null>(null);

  const handlePoLogoSameAsLogoChange = (checked: boolean) => {
    setIsPoLogoSameAsLogo(checked);
    onPoLogoSameAsLogo(checked);
  };

  const handleUploadClick = (inputRef: React.RefObject<HTMLInputElement>) => {
    inputRef.current?.click();
  };

  const createFileChangeHandler = (
    setUploadedFile: React.Dispatch<React.SetStateAction<FileUploadInfo | null>>,
    onChange: (event: React.FormEvent<HTMLInputElement>) => void,
    expectedDimensions: ImageDimensions,
    setError: React.Dispatch<React.SetStateAction<string | null>>,
  ) => {
    return (event: React.FormEvent<HTMLInputElement>) => {
      const input = event.currentTarget;
      const file = input.files?.[0];
      if (!file) {
        return;
      }

      setError(null);

      getImageDimensions(file).then(
        (dimensions) => {
          if (!matchesAspectRatio(dimensions, expectedDimensions)) {
            setError(translate('administration.company.information.whiteLabeling.dimension.error', {
              width: expectedDimensions.width,
              height: expectedDimensions.height,
            }));
            input.value = '';
            return;
          }

          const fileSizeKB = (file.size / 1024).toFixed(0);
          setUploadedFile({
            name: file.name,
            size: `${fileSizeKB} KB`,
          });
          const syntheticEvent: React.FormEvent<HTMLInputElement> = { currentTarget: input } as React.FormEvent<HTMLInputElement>;
          onChange(syntheticEvent);
        },
        () => {
          setError(translate('administration.company.information.whiteLabeling.dimension.error', {
            width: expectedDimensions.width,
            height: expectedDimensions.height,
          }));
          input.value = '';
        },
      );
    };
  };

  const getFileExtension = (fileName: string) => {
    return fileName.split('.').pop()?.toUpperCase() || 'FILE';
  };

  const isLoading = asyncState?.status === AsyncStatus.Active || asyncS3linkState?.status === AsyncStatus.Active;

  const renderUploadSection = (
    title: string,
    uploadedFile: FileUploadInfo | null,
    inputRef: React.RefObject<HTMLInputElement>,
    onChange: (event: React.FormEvent<HTMLInputElement>) => void,
    s3link: string | undefined,
    setUploadedFile: React.Dispatch<React.SetStateAction<FileUploadInfo | null>>,
    expectedDimensions: ImageDimensions,
    error: string | null,
    setError: React.Dispatch<React.SetStateAction<string | null>>,
  ) => {
    const handleFileChange = createFileChangeHandler(
      setUploadedFile,
      onChange,
      expectedDimensions,
      setError,
    );

    return (
      <Box mb={2}>
        <Typography className={classes.label}>
          {title}
        </Typography>
        <input
          ref={inputRef}
          type="file"
          accept="image/png,image/jpeg,image/svg+xml,.svg"
          onChange={handleFileChange}
          className={classes.fileInput}
          title={translate('administration.company.information.whiteLabeling.chooseFile')}
          aria-label={translate('administration.company.information.whiteLabeling.chooseFile')}
          disabled={isLoading}
        />

        {uploadedFile ? (
          <Box>
            <Box className={classes.fileInfo}>
              <Box className={classes.fileIcon}>
                {isLoading ? (
                  <CircularProgress size={16} thickness={4} />
                ) : (
                  getFileExtension(uploadedFile.name)
                )}
              </Box>
              <Box className={classes.fileDetails}>
                <Typography className={classes.fileName}>
                  {uploadedFile.name}
                </Typography>
                <Typography className={classes.fileSize}>
                  {uploadedFile.size}
                </Typography>
              </Box>
            </Box>
            {!isLoading && (
              <Box
                className={classes.uploadArea}
                onClick={() => handleUploadClick(inputRef)}
                role="button"
                tabIndex={0}
                onKeyDown={(e) => {
                  if (e.key === 'Enter' || e.key === ' ') {
                    handleUploadClick(inputRef);
                  }
                }}
                aria-label={translate('administration.company.information.whiteLabeling.replaceAriaLabel', { title: title.toLowerCase() })}
              >
                <Typography className={classes.uploadText}>
                  {translate('administration.company.information.whiteLabeling.replace')}
                </Typography>
              </Box>
            )}
          </Box>
        ) : (
          <Box
            className={classes.uploadArea}
            onClick={() => handleUploadClick(inputRef)}
            role="button"
            tabIndex={0}
            onKeyDown={(e) => {
              if (e.key === 'Enter' || e.key === ' ') {
                handleUploadClick(inputRef);
              }
            }}
            aria-label={translate('administration.company.information.whiteLabeling.uploadAriaLabel', { title: title.toLowerCase() })}
          >
            <Typography className={classes.uploadIcon}>📁</Typography>
            <Typography className={classes.uploadText}>
              {translate('administration.company.information.whiteLabeling.dragDrop', { title: title.toLowerCase() })}
            </Typography>
            <Typography className={classes.uploadSubtext}>
              {translate('administration.company.information.whiteLabeling.fileTypes')}
            </Typography>
          </Box>
        )}

        {s3link && (
          <Box mt={2}>
            <Typography variant="caption" color="textSecondary">
              {translate('administration.company.information.whiteLabeling.currentFile')}
            </Typography>
            <Typography variant="body2" style={{ marginTop: 4 }}>
              <a href={s3link} target="_blank" rel="noopener noreferrer">
                {s3link.split('/').pop()}
              </a>
            </Typography>
          </Box>
        )}

        {error && (
          <Typography variant="body2" className={classes.errorText}>
            {error}
          </Typography>
        )}
      </Box>
    );
  };

  return (
    <Box>
      {renderUploadSection(
        translate('administration.company.information.whiteLabeling.logo.upload'),
        logoUploadedFile,
        logoInputRef,
        onLogoChange,
        whiteLabel.logo,
        setLogoUploadedFile,
        LOGO_DIMENSIONS,
        logoError,
        setLogoError,
      )}
      {renderUploadSection(
        translate('administration.company.information.whiteLabeling.avatar.upload'),
        avatarUploadedFile,
        avatarInputRef,
        onAvatarChange,
        whiteLabel.avatar,
        setAvatarUploadedFile,
        AVATAR_DIMENSIONS,
        avatarError,
        setAvatarError,
      )}
      <Box>
        {renderUploadSection(
          translate('administration.company.information.whiteLabeling.poLogo.upload'),
          poLogoUploadedFile,
          poLogoInputRef,
          onPoLogoChange,
          whiteLabel.poLogo,
          setPoLogoUploadedFile,
          PO_LOGO_DIMENSIONS,
          poLogoError,
          setPoLogoError,
        )}
        <FormControlLabel
          control={
            <Checkbox
              checked={isPoLogoSameAsLogo}
              onChange={(event) => handlePoLogoSameAsLogoChange(event.target.checked)}
              disabled={isLoading || !whiteLabel.logo}
            />
          }
          label={translate('administration.company.information.whiteLabeling.poLogo.sameAsLogo')}
        />
      </Box>
    </Box>
  );
};

export default WhiteLabelingManagementPresenter;
